import { Shield, Mail, CheckCircle2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FillerModal from '@/components/Fillermodal';

import bgTexture from '@assets/images/Hero/hero-bg.png';

// ⬅️ Import API service
import { fetchCarousel } from '@/services/carouselService';

interface CarouselItem {
  id: number;
  imageUrl: string;
  order: number;
}

export default function WhyChoose() {
  const [slides, setSlides] = useState<CarouselItem[]>([]);
  const [current, setCurrent] = useState(0);
  const [loadingSlides, setLoadingSlides] = useState(true);
  const [isFillerOpen, setIsFillerOpen] = useState(false);

  // Load carousel images from API
  useEffect(() => {
    async function load() {
      try {
        const data = await fetchCarousel();
        if (Array.isArray(data)) {
          const sorted = [...data].sort((a, b) => a.order - b.order);
          setSlides(sorted);
        }
      } catch (err) {
        console.error('Carousel load failed', err);
      } finally {
        setLoadingSlides(false);
      }
    }
    load();
  }, []);

  // Auto-slide every 4 seconds
  useEffect(() => {
    if (slides.length === 0) return;

    const interval = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [slides]);

  return (
    <>
      <section
        className="relative overflow-hidden bg-[#0B0B0B] px-4 py-24 text-white"
        style={{
          backgroundImage: `url(${bgTexture})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-black/60" />

        {/* Container */}
        <div className="relative z-10 mx-auto flex w-full max-w-5xl flex-col-reverse items-center gap-8 md:flex-row md:gap-5 2xl:max-w-[1200px]">
          {/* LEFT COLUMN (TEXT) */}
          <div
            data-aos="fade-right"
            className="w-full text-center md:w-1/2 md:text-left"
          >
            <div className="mx-auto max-w-md md:mx-0">
              <h2 className="text-2xl font-extrabold tracking-tighter sm:text-3xl md:text-4xl">
                Why Choose <span className="text-[#FFBD59]">AeroComp?</span>
              </h2>

              <p className="mt-3 text-sm leading-relaxed tracking-tighter text-gray-300 sm:text-base md:text-sm">
                <span className="font-semibold text-[#FFBD59]">
                  Protection with Purpose
                </span>{' '}
                – Proudly Philippine-made armor built on innovation,
                reliability, and defense self-sufficiency.
              </p>

              <ul className="mt-6 space-y-3 text-sm sm:text-base">
                {[
                  'Proudly Philippine-Made',
                  'Designed with Advanced Composites',
                  'Reliable Ballistic Standards (Level IIIA & Level III)',
                  'Contributing to National Defense Goals',
                ].map((text, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-sm tracking-tighter"
                  >
                    <CheckCircle2 size={18} className="text-[#FFBD59]" />
                    <span>{text}</span>
                  </li>
                ))}
              </ul>

              {/* Buttons */}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-between">
                <Link
                  to="/products"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#FFBD59] px-5 py-4 text-sm font-semibold text-black shadow-md transition hover:bg-[#e6a949] sm:w-[48%]"
                >
                  <Shield size={16} />
                  See All Products
                </Link>

                <button
                  onClick={() => window.open('/appointmentsystem', '_blank')}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-[#FFBD59] px-5 py-4 text-sm font-semibold text-[#FFBD59] transition hover:bg-[#FFBD59] hover:text-black sm:w-[48%]"
                >
                  <Mail size={16} />
                  Contact Us
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN (CAROUSEL) */}
          <div
            data-aos="fade-left"
            className="flex w-full flex-col items-center md:w-1/2"
          >
            {/* Loading State */}
            {loadingSlides ? (
              <div className="aspect-video w-full max-w-xl animate-pulse rounded-lg bg-gray-700/30" />
            ) : slides.length === 0 ? (
              <div className="flex aspect-video w-full max-w-xl items-center justify-center rounded-lg bg-gray-800 text-gray-400">
                No images available.
              </div>
            ) : (
              <div className="relative aspect-video w-full max-w-xl overflow-hidden rounded-lg shadow-lg">
                {slides.map((item, idx) => (
                  <img
                    key={item.id}
                    src={item.imageUrl}
                    className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
                      idx === current ? 'opacity-100' : 'opacity-0'
                    }`}
                  />
                ))}
              </div>
            )}

            {/* Indicators */}
            {slides.length > 1 && (
              <div className="mt-3 flex justify-center gap-2">
                {slides.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrent(idx)}
                    className={`h-2 w-6 rounded-full transition-all ${
                      idx === current ? 'bg-[#FFBD59]' : 'bg-white/30'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* MODAL */}
      <FillerModal
        isOpen={isFillerOpen}
        onClose={() => setIsFillerOpen(false)}
        title="Ongoing Development!"
        message="Our APPOINTMENT SYSTEM is currently under development. You can reach us directly at"
        email=" aerocompproject@gmail.com"
        gmailComposeLink="https://mail.google.com/mail/u/0/#inbox?compose=GTvVlcSHxGmQjXQnWhJtrZqMgWJkDwXkLXvtVLmgTtpxNNPBWtgszVBntflnSCmsfHsgqBfxBScxL"
      />
    </>
  );
}
